import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    Grid,
    IconButton,
    InputAdornment,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import GlobalNotification from '../../component/common/snackbar';
import { ReactComponent as MingcuteCloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import i18n from '../../i18n';
import { cloneJobSettingsEmployeeInfo, createJobSettingsEmployeeInfoForAddEmployeeDialog } from './job-settings.data';
import type { JobSettingsEmployeeInfo } from './job-settings.types';

/**
 * 与详情页授权药丸排序一致；文案按 Figma 7325:108873 使用短标签（CWA / RWA …）。
 */
const ADD_EMPLOYEE_DIALOG_AU_OPTIONS: Array<{ infoKey: keyof JobSettingsEmployeeInfo; label: string }> = [
    { infoKey: 'cwaLogin', label: 'CWA' },
    { infoKey: 'rwaLogin', label: 'RWA' },
    { infoKey: 'kwaLogin', label: 'KWA' },
    { infoKey: 'copLogin', label: 'COP' },
    { infoKey: 'mopLogin', label: 'MOP' },
    { infoKey: 'eccoPosLogin', label: 'ECCO POS' },
];

/** 新增员工弹窗主表单行配置（顺序与 Figma 7325:108873 一致） */
type AddEmployeeFormRow =
    | {
        rowKey: string;
        labelKey: string;
        kind: 'text';
        infoKey: keyof JobSettingsEmployeeInfo;
        required?: boolean;
    }
    | { rowKey: string; labelKey: string; kind: 'ssn'; infoKey: 'ssnSin' }
    | { rowKey: string; labelKey: string; kind: 'password'; infoKey: 'passwordId' }
    | { rowKey: string; labelKey: string; kind: 'number'; infoKey: keyof JobSettingsEmployeeInfo }
    | { rowKey: string; labelKey: string; kind: 'role' };

/** 编辑弹窗只读行：展示编号 / 创建时间（禁用输入） */
type ReadonlyPlainSource = 'employeeId' | 'createdAt';

/** 新增与编辑共用表单行（编辑时在顶部追加只读行） */
export type EmployeeDialogFormRow =
    | AddEmployeeFormRow
    | { rowKey: string; labelKey: string; kind: 'readonlyPlain'; plainSource: ReadonlyPlainSource };

const ADD_EMPLOYEE_FORM_ROWS: AddEmployeeFormRow[] = [
    {
        rowKey: 'employeeLastName',
        labelKey: 'staff_setting_page_employee_name',
        kind: 'text',
        infoKey: 'employeeLastName',
        required: true,
    },
    { rowKey: 'nameSuffix', labelKey: 'job_settings_add_modal_label_nickname', kind: 'text', infoKey: 'nameSuffix' },
    {
        rowKey: 'checkName',
        labelKey: 'job_settings_add_modal_label_name_on_bill',
        kind: 'text',
        infoKey: 'checkName',
    },
    { rowKey: 'ssnSin', labelKey: 'staff_setting_page_ssn_sin', kind: 'ssn', infoKey: 'ssnSin' },
    { rowKey: 'passwordId', labelKey: 'google_login_page_text_password', kind: 'password', infoKey: 'passwordId' },
    { rowKey: 'pcApsId', labelKey: 'staff_setting_page_pc_aps_id', kind: 'number', infoKey: 'pcApsId' },
    { rowKey: 'role', labelKey: 'job_settings_col_employee_permission', kind: 'role' },
    { rowKey: 'commission', labelKey: 'staff_setting_page_commission', kind: 'number', infoKey: 'commission' },
];

/** 新增 `add` / 编辑 `edit`（编辑多出员工 ID、创建时间只读行） */
export type JobSettingsEmployeeFormVariant = 'add' | 'edit';

/**
 * 根据模式返回表单行列表（编辑含顶部只读字段）。
 */
export function getEmployeeDialogFormRows(variant: JobSettingsEmployeeFormVariant): EmployeeDialogFormRow[] {
    if (variant === 'edit') {
        return [
            {
                rowKey: 'readonlyEmployeeId',
                labelKey: 'staff_setting_page_employee_id',
                kind: 'readonlyPlain',
                plainSource: 'employeeId',
            },
            {
                rowKey: 'readonlyCreatedAt',
                labelKey: 'job_settings_employee_created_at',
                kind: 'readonlyPlain',
                plainSource: 'createdAt',
            },
            ...ADD_EMPLOYEE_FORM_ROWS,
        ];
    }
    return ADD_EMPLOYEE_FORM_ROWS;
}

export type JobSettingsAddEmployeeDialogProps = {
    /** `add`：新增员工；`edit`：编辑员工（多出只读编号与时间） */
    variant?: JobSettingsEmployeeFormVariant;
    /** 编辑模式：列表展示用员工编号 */
    readOnlyEmployeeId?: string;
    /** 编辑模式：创建时间文案（接口无时为空） */
    readOnlyEmployeeCreatedAt?: string;
    /** 编辑模式：打开时表单初始快照（深拷贝在 effect 内完成） */
    initialEmployeeInfo?: JobSettingsEmployeeInfo;
    /** 编辑模式：岗位下拉初始值 */
    initialRoleValue?: string;
    /** 弹窗是否打开 */
    open: boolean;
    /** 关闭弹窗（取消或提交成功后由父级关闭） */
    onClose: () => void;
    /** 岗位下拉选项 */
    roleSelectItems: { value: string; label: string }[];
    /** 打开时默认选中的岗位 id 字符串 */
    defaultRoleValue: string;
    /** 是否可向 POST 写入 AU 登录字段（与详情 `auLoginFieldsPatchable` 规则一致） */
    auLoginFieldsPatchable: boolean;
    /** 提交中：禁用交互 */
    submitting: boolean;
    /** 用户点「确定」且名称校验通过后：由父级发起 POST */
    onConfirm: (state: { info: JobSettingsEmployeeInfo; roleValue: string }) => Promise<void>;
};

/** CommonInput 去框线、右对齐粗体主题色，贴近设计稿数值区 */
const addDialogInputSlotSx = {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiInputBase-root': { backgroundColor: 'transparent' },
    '& .MuiInputBase-input': {
        textAlign: 'right',
        fontWeight: 700,
        color: 'var(--fill-primary-300)',
        paddingRight: 0,
    },
    '& .MuiSelect-select': {
        textAlign: 'right',
        fontWeight: 700,
        color: 'var(--fill-primary-300)',
    },
};

/**
 * 岗位设置页「新增员工」弹窗：与 Figma 7325:108873 布局一致，提交数据供 `post_employee_information` 使用。
 */
export default function JobSettingsAddEmployeeDialog(props: JobSettingsAddEmployeeDialogProps) {
    const {
        variant = 'add',
        readOnlyEmployeeId = '',
        readOnlyEmployeeCreatedAt = '',
        initialEmployeeInfo,
        initialRoleValue = '',
        open,
        onClose,
        roleSelectItems,
        defaultRoleValue,
        auLoginFieldsPatchable,
        submitting,
        onConfirm,
    } = props;

    /** 当前模式下列顺序（编辑含只读行） */
    const formRows = getEmployeeDialogFormRows(variant);

    /** 弹窗内编辑的员工信息 */
    const [employeeInfo, setEmployeeInfo] = useState<JobSettingsEmployeeInfo>(() =>
        createJobSettingsEmployeeInfoForAddEmployeeDialog(),
    );
    /** 岗位下拉当前值 */
    const [roleValue, setRoleValue] = useState<string>(defaultRoleValue);
    /** 密码是否明文 */
    const [passwordVisible, setPasswordVisible] = useState(false);

    /** 打开时重置表单与岗位（编辑用初始快照，新增用工厂默认值） */
    useEffect(() => {
        if (!open) {
            return;
        }
        if (variant === 'edit') {
            setEmployeeInfo(
                initialEmployeeInfo != null
                    ? cloneJobSettingsEmployeeInfo(initialEmployeeInfo)
                    : createJobSettingsEmployeeInfoForAddEmployeeDialog(),
            );
            setRoleValue(initialRoleValue);
        } else {
            setEmployeeInfo(createJobSettingsEmployeeInfoForAddEmployeeDialog());
            setRoleValue(defaultRoleValue);
        }
        setPasswordVisible(false);
    }, [open, variant, defaultRoleValue, initialEmployeeInfo, initialRoleValue]);

    /** 合并局部字段更新 */
    const patchInfo = useCallback((patch: Partial<JobSettingsEmployeeInfo>) => {
        setEmployeeInfo((prev) => ({ ...prev, ...patch }));
    }, []);

    /** 点确定：校验名称非空后交给父级 POST */
    const handleConfirm = useCallback(async () => {
        if (!employeeInfo.employeeLastName.trim()) {
            GlobalNotification.error(i18n.t('staff_setting_page_please_fill_in_the_employee_name'));
            return;
        }
        await onConfirm({ info: employeeInfo, roleValue });
    }, [employeeInfo, roleValue, onConfirm]);

    const handleCloseRequest = useCallback(() => {
        if (submitting) {
            return;
        }
        onClose();
    }, [submitting, onClose]);

    /** 公共 CommonInput 外观（无边框、小号） */
    const thinInputProps = {
        hiddenLabel: true,
        fullWidth: true,
        size: 'small' as const,
        variant: 'outlined' as const,
        sx: addDialogInputSlotSx,
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (submitting) {
                    return;
                }
                if (reason === 'backdropClick') {
                    handleCloseRequest();
                }
                if (reason === 'escapeKeyDown') {
                    handleCloseRequest();
                }
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'var(--fill-neutral-100)',
                    maxWidth: 920,
                    /** 小屏限制弹窗高度，内部表单区滚动、底栏固定 */
                    maxHeight: 'calc(100vh - 32px)',
                    display: 'flex',
                    flexDirection: 'column',
                    m: 2,
                },
            }}
        >
            {/** 内边距与设计稿一致：上 16px、左右下 24px；中间表单区可滚动 */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    pt: '16px',
                    px: '24px',
                    pb: '24px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexShrink: 0,
                        mb: '24px',
                    }}
                >
                    <Typography variant="h3" component="h2" color="text.primary">
                        {i18n.t(variant === 'edit' ? 'job_settings_edit_employee' : 'job_settings_add_employee')}
                    </Typography>
                    <IconButton
                        type="button"
                        aria-label={i18n.t('extra_cost_text_close')}
                        onClick={handleCloseRequest}
                        disabled={submitting}
                        size="small"
                        sx={{ color: 'var(--text-dark)' }}
                    >
                        <MingcuteCloseIcon width={24} height={24} aria-hidden />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        WebkitOverflowScrolling: 'touch',
                    }}
                >
                {/** 行列间距均为 24px（theme.spacing(3)，对齐设计 gap-[24px]）；单元格与员工详情共用 FormFieldCell + 描边 300 */}
                <Grid container spacing={3}>
                    {formRows.map((def) => {
                        let control: ReactNode;

                        if (def.kind === 'readonlyPlain') {
                            const plainText =
                                def.plainSource === 'employeeId' ? readOnlyEmployeeId : readOnlyEmployeeCreatedAt;
                            control = (
                                <CommonInput
                                    inputType="INPUT"
                                    {...thinInputProps}
                                    disabled
                                    value={plainText}
                                    onChange={() => undefined}
                                />
                            );
                        } else if (def.kind === 'text') {
                            control = (
                                <CommonInput
                                    inputType="INPUT"
                                    {...thinInputProps}
                                    required={def.required}
                                    regularType={def.required ? 'required' : undefined}
                                    value={employeeInfo[def.infoKey] as string}
                                    onChange={(e) =>
                                        patchInfo({
                                            [def.infoKey]: (e.target as HTMLInputElement).value,
                                        } as Partial<JobSettingsEmployeeInfo>)}
                                    disabled={submitting}
                                />
                            );
                        } else if (def.kind === 'ssn') {
                            control = (
                                <CommonInput
                                    inputType="INPUT"
                                    {...thinInputProps}
                                    value={employeeInfo.ssnSin}
                                    onChange={(e) =>
                                        patchInfo({
                                            ssnSin: (e.target as HTMLInputElement).value.replace(/[^\dxX]/g, ''),
                                        })}
                                    disabled={submitting}
                                />
                            );
                        } else if (def.kind === 'password') {
                            control = (
                                <CommonInput
                                    inputType="INPUT"
                                    type={passwordVisible ? 'text' : 'password'}
                                    {...thinInputProps}
                                    autoComplete="new-password"
                                    value={employeeInfo.passwordId}
                                    onChange={(e) => patchInfo({ passwordId: (e.target as HTMLInputElement).value })}
                                    disabled={submitting}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setPasswordVisible((v) => !v)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    edge="end"
                                                    size="small"
                                                    color="primary"
                                                    disabled={submitting}
                                                >
                                                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            );
                        } else if (def.kind === 'number') {
                            control = (
                                <CommonInput
                                    inputType="INPUT"
                                    type="number"
                                    {...thinInputProps}
                                    value={employeeInfo[def.infoKey] as string}
                                    onChange={(e) =>
                                        patchInfo({
                                            [def.infoKey]: (e.target as HTMLInputElement).value,
                                        } as Partial<JobSettingsEmployeeInfo>)}
                                    disabled={submitting}
                                />
                            );
                        } else {
                            control = (
                                <CommonInput
                                    inputType="SELECT"
                                    {...thinInputProps}
                                    value={roleValue}
                                    items={roleSelectItems}
                                    onChange={(e) => setRoleValue(String((e.target as HTMLInputElement).value))}
                                    disabled={submitting || roleSelectItems.length === 0}
                                />
                            );
                        }

                        return (
                            <Grid item xs={12} sm={6} key={def.rowKey} sx={{ minWidth: 0 }}>
                                <FormFieldCell
                                    labelKey={def.labelKey}
                                    select={'kind' in def && def.kind === 'role'}
                                    strokePrimary="300"
                                >
                                    {control}
                                </FormFieldCell>
                            </Grid>
                        );
                    })}
                </Grid>

                <Typography variant="h3" color="text.primary">
                    {i18n.t('staff_setting_page_projects')}
                </Typography>

                {/** 授权项目：Figma 7325:108873 — gap 24、py16 px24、圆角 8；选中填充图标按钮主题色 300 + 描边主题色 100 + 投影 */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
                    {ADD_EMPLOYEE_DIALOG_AU_OPTIONS.map((opt) => {
                        const active = employeeInfo[opt.infoKey] !== 0;
                        return (
                            <Button
                                key={opt.infoKey}
                                type="button"
                                variant="outlined"
                                disabled={submitting || !auLoginFieldsPatchable}
                                onClick={() => {
                                    patchInfo({
                                        [opt.infoKey]: active ? 0 : 1,
                                    } as Partial<JobSettingsEmployeeInfo>);
                                }}
                                sx={{
                                    textTransform: 'none',
                                    minWidth: 0,
                                    px: '24px',
                                    py: '16px',
                                    borderRadius: '8px',
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    borderColor: 'var(--stroke-primary-100)',
                                    ...(active
                                        ? {
                                            bgcolor: 'var(--icon-primary-300)',
                                            color: 'var(--text-light)',
                                            boxShadow: '0 0 2px var(--shadow-25)',
                                            '&:hover': {
                                                bgcolor: 'var(--icon-primary-300)',
                                                borderColor: 'var(--stroke-primary-100)',
                                                opacity: 0.92,
                                            },
                                        }
                                        : {
                                            bgcolor: 'var(--fill-neutral-100)',
                                            color: 'var(--text-primary-300)',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: 'var(--fill-neutral-200)',
                                                borderColor: 'var(--stroke-primary-100)',
                                            },
                                        }),
                                }}
                            >
                                <Typography variant="subtitle1" component="span" sx={{ color: 'inherit', lineHeight: '24px' }}>
                                    {opt.label}
                                </Typography>
                            </Button>
                        );
                    })}
                </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '24px',
                        flexShrink: 0,
                        mt: '24px',
                    }}
                >
                    <Button
                        type="button"
                        variant="outlined"
                        disabled={submitting}
                        onClick={handleCloseRequest}
                        sx={{
                            flex: '1 1 200px',
                            minHeight: 64,
                            textTransform: 'none',
                            borderColor: 'var(--stroke-neutral-800-a25)',
                            color: 'var(--text-secondary)',
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-secondary)' }}>
                            {i18n.t('general_button_cancel')}
                        </Typography>
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        onClick={() => void handleConfirm()}
                        sx={{ flex: '1 1 200px', minHeight: 64, textTransform: 'none' }}
                    >
                        <Typography variant="subtitle1" sx={{ color: 'inherit' }}>
                            {i18n.t('general_button_confirm')}
                        </Typography>
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
